/**
 * WHICH STAFF FIRM A STAFF-SECTION TAB CLAIMS (STAFF-G5b; spec multi-product
 * 022 G48, G49 (1); D49: the platform keeps the firm in the per-tab
 * active-firm store and sends it as `x-cova-org`).
 *
 * `/cs` and `/super-admin` are the staff shell's sections (`scopeForPath`
 * answers `staff` for both), so their requests claim the tab's STAFF-scope
 * firm. A tab that opens one with no staff claim yet carries whatever firm the
 * server picked for an unclaimed read, which for a staffer who is also in a
 * brokerage may be the brokerage. This decides what the tab does about it:
 *
 *  - the acting firm is one of the person's staff firms: nothing to do;
 *  - the person is in no staff firm: nothing to do (today's path; the routes'
 *    own gates and the server still refuse anyone who is not staff);
 *  - exactly one staff firm: claim it, and wait for identity to answer as it;
 *  - several: no pick is made for them; the Staff frame's "Switch to Cova
 *    Staff" prompt (the existing picker) lists them;
 *  - the claim was sent and identity still answered another firm: the server
 *    did not admit it, so the frame's prompt shows. Never a fall back to a
 *    non-staff firm, and never a second claim of the same firm.
 *
 * Pure, so every case runs with no React, store, router or network.
 */

export type StaffFirmClaim =
  /** Identity or the membership directory has not answered yet. */
  | { readonly kind: "pending" }
  /** The acting firm is one of the person's staff firms. */
  | { readonly kind: "acting" }
  /** The person is in no staff firm. */
  | { readonly kind: "no-staff-firm" }
  /** Several staff firms: the person picks. */
  | { readonly kind: "pick" }
  /** Claim this, the person's one staff firm, for the staff scope. */
  | { readonly kind: "claim"; readonly orgId: string }
  /** Claimed; identity is re-reading under the claim. */
  | { readonly kind: "claiming"; readonly orgId: string }
  /** Claimed, and identity answered another firm: the server did not admit it. */
  | { readonly kind: "refused"; readonly orgId: string }

export interface StaffFirmClaimInput {
  /** The person's staff-scope firm ids from the membership directory; null until it answers. */
  readonly staffFirmIds: ReadonlyArray<string> | null
  /** The firm identity answered for the staff scope; undefined until it answers, null mid-onboarding. */
  readonly actingOrgId: string | null | undefined
  /** The firm this tab's staff-scope requests claim (`activeFirmFor(staff)`), or null. */
  readonly claimedOrgId: string | null
  /** An identity read is in flight. */
  readonly identityFetching: boolean
}

const lower = (orgId: string): string => orgId.toLowerCase()

export const staffFirmClaimOf = (input: StaffFirmClaimInput): StaffFirmClaim => {
  if (input.staffFirmIds === null || input.actingOrgId === undefined) return { kind: "pending" }
  const staffFirms = [...new Set(input.staffFirmIds.map(lower))]
  const acting = input.actingOrgId === null ? null : lower(input.actingOrgId)
  if (acting !== null && staffFirms.includes(acting)) return { kind: "acting" }
  const [only, ...others] = staffFirms
  if (only === undefined) return { kind: "no-staff-firm" }
  if (others.length > 0) return { kind: "pick" }
  const claimed = input.claimedOrgId === null ? null : lower(input.claimedOrgId)
  if (claimed !== only) return { kind: "claim", orgId: only }
  return input.identityFetching ? { kind: "claiming", orgId: only } : { kind: "refused", orgId: only }
}

/** While a claim is made or awaited, the frame shows its busy region rather than the wrong firm. */
export const isClaimingStaffFirm = (claim: StaffFirmClaim): boolean =>
  claim.kind === "claim" || claim.kind === "claiming"
